import { PayloadAction } from '@reduxjs/toolkit'
import { setStatsStatus } from 'features/account/state/accountSlice'
import { RequestStatus } from 'features/common'
import { createApiError } from 'features/common/api/apiError'
import { isHardQuotaError } from 'features/quotas/utils'
import { call, put, takeEvery } from 'redux-saga/effects'
import { dryRunDuplicateMapRequest, duplicateMapRequest } from '../api/maps'
import { DuplicateMapProps, DuplicateMapType } from '../types'
import { dryRunDuplicateMapAction, duplicateMapAction } from './duplicateMapSlice'
import {
  setDryRunDuplicateMapStatus,
  setDuplicateMapError,
  setDuplicateMapStatus,
  setDuplicatingMapId,
  setDuplicateMapType,
  setUnavailableConnections,
  setLatestMapsStatus,
  setMyMapsStatus
} from './mapsSlice'
import { openNewWindow } from 'features/common/utils/navigationUtils'
import { BuilderPaths } from 'features/builder/paths'

function* dryRunClone(action: PayloadAction<DuplicateMapProps>) {
  try {
    yield put(setDryRunDuplicateMapStatus(RequestStatus.Pending))
    yield put(setDuplicatingMapId(action.payload.mapId))
    const response = yield call(dryRunDuplicateMapRequest, action.payload)
    if (response?.status === DuplicateMapType.Partial && response.restricted_connections?.length) {
      yield put(setUnavailableConnections(response.restricted_connections))
    } else {
      yield call(clone, action)
    }
    yield put(setDuplicateMapType(response.status))
    yield put(setDryRunDuplicateMapStatus(RequestStatus.Fulfilled))
    yield put(setDuplicatingMapId(null))
  } catch (e) {
    yield put(setDryRunDuplicateMapStatus(RequestStatus.Rejected))
    yield put(setDuplicateMapError(createApiError(e)))
    yield put(setDuplicatingMapId(null))
  }
}

function* clone(action: PayloadAction<DuplicateMapProps>) {
  try {
    yield put(setDuplicatingMapId(action.payload.mapId))
    yield put(setDuplicateMapStatus(RequestStatus.Pending))
    const response = yield call(duplicateMapRequest, action.payload)
    yield put(setUnavailableConnections(response.unavailableConnections))
    yield put(setDuplicateMapStatus(RequestStatus.Fulfilled))
    yield put(setMyMapsStatus(RequestStatus.Idle))
    yield put(setStatsStatus(RequestStatus.Idle))
    yield put(setLatestMapsStatus(RequestStatus.Idle))
    yield put(setDuplicatingMapId(null))
    yield call(openNewWindow, BuilderPaths.EditMap(response.id))
  } catch (e) {
    yield put(setDuplicateMapStatus(RequestStatus.Rejected))
    const apiError = createApiError(e)
    yield put(setDuplicateMapError(apiError))
    yield put(setDuplicatingMapId(null))
    if (isHardQuotaError(apiError)) {
      yield put(setStatsStatus(RequestStatus.Idle))
    }
  }
}

export function* watchDuplicateMap() {
  yield takeEvery(dryRunDuplicateMapAction.type, dryRunClone)
  yield takeEvery(duplicateMapAction.type, clone)
}
